import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';

interface UsernameAvailabilityProps {
  value: string;
  /** Skip the check when value equals this current value (used when editing). */
  excludeCurrent?: string;
  onValidityChange?: (valid: boolean) => void;
}

type Status = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'reserved';

interface AvailabilityResponse {
  username: string;
  available: boolean;
  reason: 'OK' | 'TAKEN' | 'INVALID_FORMAT' | 'RESERVED';
}

export default function UsernameAvailability({ value, excludeCurrent, onValidityChange }: UsernameAvailabilityProps) {
  const [status, setStatus] = useState<Status>('idle');
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) {
      setStatus('idle');
      onValidityChange?.(false);
      return;
    }
    if (excludeCurrent && trimmed === excludeCurrent.toLowerCase()) {
      setStatus('idle');
      onValidityChange?.(false);
      return;
    }
    setStatus('checking');
    const timer = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const res = await api.get<AvailabilityResponse>(
          `/api/auth/username/availability?username=${encodeURIComponent(trimmed)}`
        );
        if (ctrl.signal.aborted) return;
        if (res.available) { setStatus('available'); onValidityChange?.(true); }
        else if (res.reason === 'INVALID_FORMAT') { setStatus('invalid'); onValidityChange?.(false); }
        else if (res.reason === 'RESERVED') { setStatus('reserved'); onValidityChange?.(false); }
        else { setStatus('taken'); onValidityChange?.(false); }
      } catch {
        setStatus('idle');
        onValidityChange?.(true);
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, excludeCurrent]);

  if (status === 'idle') return null;
  const text =
    status === 'checking' ? 'Checking availability…' :
    status === 'available' ? 'Username available' :
    status === 'taken' ? 'Username already taken' :
    status === 'reserved' ? 'This username is reserved' :
    'Username format invalid (4–30 chars; letters, digits, dot, underscore, hyphen; must start with a letter)';

  return (
    <div className={`username-availability username-availability--${status}`} aria-live="polite">
      {text}
    </div>
  );
}
